import { ApolloClient, type FetchResult } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Details,
  Divider,
  EmptyStation,
  type FormActionData,
  InfoPanel,
  Loader,
  MessageBar,
  Paragraph,
  ReadOnlyField,
  Section,
  type StationMessage,
  TextButton,
} from '@axinom/mosaic-ui';
import clsx from 'clsx';
import { config } from 'config';
import { Field } from 'formik';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  DisableStorageDocument,
  type DisableStorageMutation,
  EnableStorageDocument,
  type EnableStorageMutation,
  HostingServiceConfigurationsDocument,
  type HostingServiceConfigurationsQuery,
  type ObjectStorage,
  ObjectStorageDetailsDocument,
  type ObjectStorageDetailsQuery,
  type ObjectStorageDetailsQueryPayload,
  ProviderType,
  RegenerateObjectStorageConnectionStringDocument,
  type RegenerateObjectStorageConnectionStringMutation,
  ResourceStatus,
  StorageAccountDocument,
  type StorageAccountQuery,
} from '../../../../generated/hosting-service';

import { ensureError } from '../../../../common/errors/ensure-error';
import {
  formatEnumValueToTitleCase,
  getManagedServiceAvailabilityMessage,
} from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';
import { resolveStationMessage } from '../HostingServiceConfiguration/ServiceConfigurationMessages';
import { LabeledIconField } from '../common/components/LabeledIconField/LabeledIconField';
import { getStorageStateIconLabel } from '../common/utils';
import classes from './CloudStorageAccountDetails.module.scss';
import {
  CredentialsCanNotBeRegenerated,
  GenerationRequiredParametersAreNotDefined,
  SaveConnectionValueSecurely,
} from './StorageDetailsStationMessages';

type StorageAudit = Pick<
  ObjectStorage,
  | 'id'
  | 'createdDate'
  | 'createdUser'
  | 'updatedDate'
  | 'updatedUser'
  | 'tenantId'
  | 'environmentId'
>;

type StorageCredentials = Pick<
  ObjectStorage,
  'name' | 'azureConnectionString' | 'azureStorageAccountKey'
>;
type StorageParameters = Pick<ObjectStorage, 'provider' | 'status'>;

type StorageAccountInfo = StorageAudit & StorageCredentials & StorageParameters;

type StorageAccountDetails = Pick<
  ObjectStorageDetailsQueryPayload,
  'containerCount' | 'storageAccountConsumptionInBytes'
>;

const DEFAULT_STORAGE_PROVIDER = ProviderType.Azure;

export const CloudStorageAccountDetails: React.FC = () => {
  const { envId } = useParams<{
    envId: string;
    action: string;
  }>();

  const [storageDetails, setStorageDetails] = useState<StorageAccountInfo>();

  const [storageAccountMetadata, setStorageAccountMetadata] =
    useState<StorageAccountDetails | null>();

  const [message, setMessage] = useState<StationMessage>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  const envApolloClient = useRef<ApolloClient<Record<string, unknown>>>();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Check the status of hosting-service
        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.HOSTING_SERVICE,
          envId,
        );
        envApolloClient.current = environmentApolloClient(
          envId,
          new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
        );

        if (statusMessage !== null) {
          setServiceStatusMessage(statusMessage);
        } else {
          // Get Hosting service configuration data.
          const hostingServiceConfigurationResponse =
            await envApolloClient.current.query<HostingServiceConfigurationsQuery>(
              {
                query: HostingServiceConfigurationsDocument,
                fetchPolicy: 'no-cache',
              },
            );

          const hostingServiceData =
            hostingServiceConfigurationResponse?.data?.serviceConfigurations
              ?.nodes[0];

          const storageAccountDetailsResult =
            await envApolloClient.current.query<StorageAccountQuery>({
              query: StorageAccountDocument,
              fetchPolicy: 'network-only',
            });

          const storageAccountDetails =
            storageAccountDetailsResult?.data?.objectStorages?.nodes[0];

          if (!storageAccountDetails) {
            setStorageDetails({
              id: '',
              name: '',
              createdDate: '',
              createdUser: '',
              updatedDate: hostingServiceData?.updatedDate,
              updatedUser: hostingServiceData?.updatedUser
                ? hostingServiceData?.updatedUser
                : '',
              tenantId: hostingServiceData?.tenantId,
              environmentId: hostingServiceData?.environmentId,
              status: ResourceStatus.DeleteCompleted,
              provider: DEFAULT_STORAGE_PROVIDER,
            });

            return;
          } else {
            setStorageDetails(storageAccountDetails);

            const stationMessage = resolveStationMessage(
              storageAccountDetails.status,
            );
            setMessage(stationMessage);
          }

          if (storageDetails?.status === 'READY') {
            envApolloClient.current
              .query<ObjectStorageDetailsQuery>({
                query: ObjectStorageDetailsDocument,
                variables: {
                  input: {
                    provider: storageDetails?.provider,
                    storageAccountName: storageDetails?.name,
                  },
                },
                fetchPolicy: 'network-only',
              })
              .then((storageAccountMetadataResponse) => {
                setStorageAccountMetadata(
                  storageAccountMetadataResponse?.data?.objectStorageDetails,
                );
              })
              .catch((_) => {
                setStorageAccountMetadata(null);
              });
          }
        }
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [
    envApolloClient,
    envId,
    storageDetails?.name,
    storageDetails?.provider,
    storageDetails?.status,
  ]);

  const [displayConnectionString, displayConnectionStringValue] =
    useState<boolean>(false);

  const [showLoading, displayLoadingIndicator] = useState<boolean>(false);

  const regenerateConnectionDetails = async (): Promise<void> => {
    displayLoadingIndicator(true);

    if (!storageDetails?.provider || !storageDetails?.name) {
      setMessage(GenerationRequiredParametersAreNotDefined);
      displayLoadingIndicator(false);
      return;
    }

    const regenerationResult =
      await envApolloClient.current?.mutate<RegenerateObjectStorageConnectionStringMutation>(
        {
          mutation: RegenerateObjectStorageConnectionStringDocument,
          variables: {
            input: {
              provider: storageDetails?.provider,
              storageAccountName: storageDetails?.name,
            },
          },
        },
      );

    const generatedConnectionString =
      regenerationResult?.data?.regenerateObjectStorageConnectionString
        ?.storageAccountConnectionString;

    if (!generatedConnectionString) {
      setMessage(CredentialsCanNotBeRegenerated);
      displayLoadingIndicator(false);
      return;
    }

    setStorageDetails({
      ...storageDetails,
      azureConnectionString: generatedConnectionString,
    });

    displayLoadingIndicator(false);
    displayConnectionStringValue(true);
    setMessage(SaveConnectionValueSecurely);
  };

  const { actions } = useActions(
    storageDetails !== undefined,
    envId,
    storageDetails,
    regenerateConnectionDetails,
    envApolloClient.current,
  );

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="Cloud Storage" subtitle="Details">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Details
          defaultTitle="Cloud Storage"
          subtitle="Details"
          initialData={{
            data: storageDetails || {},
            loading,
            error,
          }}
          saveData={() => Promise.resolve()}
          infoPanel={
            storageDetails ? (
              <Panel audit={storageDetails} details={storageAccountMetadata} />
            ) : undefined
          }
          stationMessage={message}
          actions={actions}
        >
          {storageDetails ? (
            <Form
              credentials={storageDetails}
              details={storageAccountMetadata}
              displayCredentials={displayConnectionString}
              loadingInProgress={showLoading}
            />
          ) : undefined}
        </Details>
      )}
    </>
  );
};

const Form: React.FC<{
  credentials: StorageAccountInfo;
  details?: StorageAccountDetails | null;
  displayCredentials: boolean;
  loadingInProgress: boolean;
}> = ({ credentials, details, displayCredentials, loadingInProgress }) => {
  const storageStatus = credentials?.status;

  const { icon, label } = getStorageStateIconLabel(storageStatus);

  return (
    <>
      <LabeledIconField
        name="status"
        label="Status"
        icon={icon}
        value={formatEnumValueToTitleCase(label)}
      />

      <Field name="provider" label="Cloud Provider" as={ReadOnlyField} />
      {storageStatus === 'READY' && (
        <>
          <Divider />
          <Field name="name" label="Storage Account Name" as={ReadOnlyField} />

          <Loader showLoader={loadingInProgress}>
            <div className={clsx(classes.password)}>
              <Field
                name="storageAccountKey"
                label="Storage Account Key"
                as={ReadOnlyField}
                value={
                  displayCredentials
                    ? credentials?.azureStorageAccountKey
                    : '*********************************'
                }
                className={clsx(classes.content_wrap)}
              />

              <TextButton
                className={classes.button}
                text="Copy"
                onButtonClicked={() =>
                  navigator.clipboard.writeText(
                    credentials?.azureStorageAccountKey ?? '',
                  )
                }
              />
            </div>
          </Loader>

          <Loader showLoader={loadingInProgress}>
            <div className={clsx(classes.password)}>
              <Field
                name="connectionString"
                label="Connection String"
                as={ReadOnlyField}
                value={
                  displayCredentials
                    ? credentials.azureConnectionString
                    : '*********************************'
                }
                className={clsx(classes.content_wrap)}
              />
              <>
                <TextButton
                  className={classes.button}
                  text="Copy"
                  onButtonClicked={() =>
                    navigator.clipboard.writeText(
                      credentials?.azureConnectionString ?? '',
                    )
                  }
                />
              </>
            </div>
          </Loader>

          <Divider />
          <Field
            name="storageAccountConsumptionBytes"
            label="Current Consumption"
            as={ReadOnlyField}
            value={transformConsumption(
              details?.storageAccountConsumptionInBytes,
            )}
          />
        </>
      )}
    </>
  );
};

function transformConsumption(sizeBytes?: number | null): string {
  if (sizeBytes === undefined) {
    return 'Loading...';
  }
  if (sizeBytes === null) {
    return 'Unknown';
  }

  const sizeInMb = sizeBytes / 1024 / 1024;
  return `${sizeInMb.toFixed(2)} MB`;
}

const Panel: React.FC<{
  audit: (StorageAudit & StorageParameters) | undefined;
  details?: StorageAccountDetails | null;
}> = ({ audit, details }) => {
  return (
    <InfoPanel>
      <Section>
        {audit?.status !== ResourceStatus.DeleteCompleted && (
          <>
            <Paragraph title="Storage ID">{audit?.id ?? ''}</Paragraph>
            <Paragraph title="Created">
              {audit
                ? new Date(audit.createdDate).toLocaleString() +
                  ' by ' +
                  audit.createdUser
                : ''}
            </Paragraph>
          </>
        )}
        <Paragraph title="Last Modified">
          {audit
            ? new Date(audit.updatedDate).toLocaleString() +
              ' by ' +
              audit.updatedUser
            : ''}
        </Paragraph>
      </Section>
      {audit?.status === ResourceStatus.Ready && (
        <Section>
          <Paragraph title="Containers">
            {details?.containerCount === null
              ? 'Unknown'
              : details?.containerCount?.toString() ?? 'Loading...'}
          </Paragraph>
        </Section>
      )}
      <Section>
        <Paragraph title="Tenant ID">{audit?.tenantId}</Paragraph>
        <Paragraph title="Environment ID">{audit?.environmentId}</Paragraph>
      </Section>
    </InfoPanel>
  );
};

function useActions(
  isActionsAvailable: boolean,
  envId: string,
  storageIdentifiers: (StorageCredentials & StorageParameters) | undefined,
  regenerateStorageCredentials: () => Promise<void>,
  envApolloClient?: ApolloClient<Record<string, unknown>>,
): {
  readonly actions: FormActionData<Partial<StorageAccountInfo>>[];
} {
  const history = useHistory();

  const storageStatus = storageIdentifiers?.status;

  const disableStorage = async (): Promise<
    FetchResult<DisableStorageMutation> | undefined
  > => {
    return envApolloClient?.mutate<DisableStorageMutation>({
      mutation: DisableStorageDocument,
      variables: {
        input: {
          provider: storageIdentifiers?.provider,
        },
      },
      fetchPolicy: 'no-cache',
    });
  };

  const enableStorage = async (): Promise<
    FetchResult<EnableStorageMutation> | undefined
  > => {
    return envApolloClient?.mutate<EnableStorageMutation>({
      mutation: EnableStorageDocument,
      variables: {
        input: {
          provider: storageIdentifiers?.provider,
        },
      },
      fetchPolicy: 'no-cache',
    });
  };

  const actions: FormActionData<Partial<StorageAccountInfo>>[] = [];

  if (
    isActionsAvailable &&
    storageStatus !== ResourceStatus.Ready &&
    storageStatus !== ResourceStatus.DeleteError &&
    storageStatus !== ResourceStatus.DeletePending
  ) {
    actions.push({
      label: 'Enable Storage',
      onActionSelected: async () => {
        await enableStorage();
        history.replace(history.location.pathname);
      },
      isDisabled: storageStatus === ResourceStatus.CreatePending,
    });
  }

  if (storageStatus === ResourceStatus.Ready) {
    actions.push({
      label: 'Containers',
      path: `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/storage/${storageIdentifiers?.provider}/${storageIdentifiers?.name}/containers`,
    });
  }

  if (isActionsAvailable) {
    actions.push({
      label: 'Storage Logs',
      path: `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/storage/storageAccountLogs`,
    });
  }

  if (storageStatus === ResourceStatus.Ready) {
    actions.push({
      label: 'Regenerate Storage Credentials',
      confirmationMode: 'Advanced',
      confirmationConfig: {
        body: 'Regenerating storage credentials will invalidate currently configured instances.',
      },
      onActionSelected: async () => {
        return regenerateStorageCredentials();
      },
    });
  }

  if (
    (isActionsAvailable && storageStatus === ResourceStatus.Ready) ||
    storageStatus === ResourceStatus.DeleteError ||
    storageStatus === ResourceStatus.DeletePending
  ) {
    actions.push({
      label: 'Disable Storage',
      confirmationMode: 'Advanced',
      confirmationConfig: {
        body: 'Disabling Cloud Storage is a destructive operation, where the Storage Account with all of its stored content will be removed. This will invalidate any usages that refer to this Storage Account including Image/Video service profiles. Please confirm to proceed.',
      },
      onActionSelected: async () => {
        await disableStorage();
        history.replace(history.location.pathname);
      },
      isDisabled: storageStatus === ResourceStatus.DeletePending,
    });
  }

  return { actions } as const;
}

export const CloudStorageAccountDetailsCrumb: BreadcrumbResolver = () =>
  'Cloud Storage';
